/**
 * Silent partner-referral capture for the early-access gate (signup v2).
 *
 * A non-UUID `?ref=` on /early-access is a PARTNER attribution code
 * (operator-issued opaque string, e.g. `KOL-ALICE`). It is persisted to
 * localStorage (first-wins, 30-day stamp) so it survives a bounce, then
 * attached to both signup paths (`otp/start` body + the X start query).
 *
 * Completely invisible in the UI by design (locked decision) — no chip, no
 * copy variation. UUID refs are NOT captured here: those belong to the
 * user-to-user referral system (middleware-owned `REFERRAL_CODE` cookie,
 * `src/middleware.ts`), which shares the `?ref=` param without collision.
 */

const STORAGE_KEY: string = 'morfi_waitlist_ref';
const MAX_AGE_MS: number = 30 * 24 * 60 * 60 * 1000;

const PARTNER_CODE_RE = /^[A-Za-z0-9_-]{2,64}$/;
// Any UUID-shaped value (all versions/variants) belongs to the user-referral
// lane, never partner attribution.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface StoredRef {
  code: string;
  storedAt: number;
}

// ALLOW-UNKNOWN: type guard narrows JSON.parse output before structural check
function isStoredRef(value: unknown): value is StoredRef {
  if (typeof value !== 'object' || value === null) return false;
  // ALLOW-UNKNOWN: narrowing object property access for the structural check below
  const v = value as Record<string, unknown>;
  return typeof v.code === 'string' && typeof v.storedAt === 'number';
}

/** Capture a partner code from the current URL into localStorage (first-wins). */
export function captureReferralFromUrl(search: string): void {
  if (typeof window === 'undefined') return;
  const ref = new URLSearchParams(search).get('ref');
  if (ref === null || !PARTNER_CODE_RE.test(ref) || UUID_RE.test(ref)) return;
  if (getStoredReferralCode() !== null) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: ref, storedAt: Date.now() }));
  } catch {
    // SILENT-CATCH: storage unavailable (private mode / quota) — partner attribution is best-effort and must never break signup.
  }
}

/** The stored partner code, or null when organic / expired / unavailable. */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // SILENT-CATCH: storage read denied (private mode) — treat as organic signup.
    return null;
  }
  if (raw === null) return null;
  // ALLOW-UNKNOWN: JSON.parse returns any; narrowed by isStoredRef below
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // SILENT-CATCH: corrupt stored ref — treat as organic signup.
    return null;
  }
  if (!isStoredRef(parsed)) return null;
  if (Date.now() - parsed.storedAt > MAX_AGE_MS) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // SILENT-CATCH: best-effort cleanup of an expired ref; failure is harmless.
    }
    return null;
  }
  return parsed.code;
}
